import { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, FileText, ThumbsUp, ThumbsDown, CheckCircle, XCircle, X } from 'lucide-react'
// ChatMessage type imported but not used directly in this file
import { useAIChat, WorkOrderContext } from '@hooks/useAIChat'
import { cn } from '@utils/cn'
import { TypingIndicator } from './TypingIndicator'
import { TypewriterText } from './TypewriterText'
import { MarkdownRenderer } from './MarkdownRenderer'

// Modal para confirmar resposta detalhada
interface DetailedResponseModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onConfirm: () => void
  readonly isLoading: boolean
}

function DetailedResponseModal({ isOpen, onClose, onConfirm, isLoading }: Readonly<DetailedResponseModalProps>) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Resposta mais detalhada?</h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-6">
          Gostaria de receber uma resposta mais detalhada e específica da IA? 
          Isto pode demorar alguns segundos adicionais.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Não
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                A processar...
              </>
            ) : (
              'Sim, quero mais detalhe'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

interface FeedbackButtonsProps {
  readonly messageId: number
  readonly feedbackUsed: boolean | null | undefined
  readonly feedbackWorked: boolean | null | undefined
  readonly onSubmitFeedback: (messageId: number, used: boolean, worked?: boolean) => Promise<void>
  readonly onRequestDetailed?: (messageId: number) => void
}

function FeedbackButtons({ messageId, feedbackUsed, feedbackWorked, onSubmitFeedback, onRequestDetailed }: Readonly<FeedbackButtonsProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localUsed, setLocalUsed] = useState<boolean | null>(feedbackUsed ?? null)

  // If feedback already submitted, show status
  if (feedbackUsed !== null && feedbackUsed !== undefined && (feedbackUsed === false || feedbackWorked !== null)) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          {feedbackUsed ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Usou as instruções</span>
              {feedbackWorked !== null && feedbackWorked !== undefined && (
                <>
                  <span className="mx-1">•</span>
                  {feedbackWorked ? (
                    <>
                      <ThumbsUp className="w-4 h-4 text-green-600" />
                      <span>Funcionou</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="w-4 h-4 text-red-600" />
                      <span>Não funcionou</span>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 text-gray-500" />
              <span>Não usou as instruções</span>
            </>
          )}
        </div>
      </div>
    )
  }

  // If user said they used it, ask if it worked
  if (localUsed === true && (feedbackWorked === null || feedbackWorked === undefined)) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs font-semibold text-gray-700 mb-2">Funcionou?</p>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setIsSubmitting(true)
              try {
                await onSubmitFeedback(messageId, true, true)
              } catch (error) {
                console.error('Erro ao enviar feedback:', error)
                alert('Erro ao enviar feedback. Tente novamente.')
              } finally {
                setIsSubmitting(false)
              }
            }}
            disabled={isSubmitting}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            Sim, funcionou
          </button>
          <button
            onClick={async () => {
              setIsSubmitting(true)
              try {
                await onSubmitFeedback(messageId, true, false)
              } catch (error) {
                console.error('Erro ao enviar feedback:', error)
                alert('Erro ao enviar feedback. Tente novamente.')
              } finally {
                setIsSubmitting(false)
              }
            }}
            disabled={isSubmitting}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ThumbsDown className="w-3.5 h-3.5" />
            Não funcionou
          </button>
        </div>
      </div>
    )
  }

  // Show initial feedback question
  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <p className="text-xs font-semibold text-gray-700 mb-2">Usou estas instruções?</p>
      <div className="flex items-center gap-2">
        <button
          onClick={async () => {
            setLocalUsed(true)
            setIsSubmitting(true)
            try {
              await onSubmitFeedback(messageId, true)
            } catch (error) {
              console.error('Erro ao enviar feedback:', error)
              alert('Erro ao enviar feedback. Tente novamente.')
              setLocalUsed(null)
            } finally {
              setIsSubmitting(false)
            }
          }}
          disabled={isSubmitting}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle className="w-3.5 h-3.5" />
          Sim, usei
        </button>
        <button
          onClick={() => {
            // Não submeter feedback directamente - mostrar modal primeiro
            if (onRequestDetailed) {
              onRequestDetailed(messageId)
            }
          }}
          disabled={isSubmitting}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircle className="w-3.5 h-3.5" />
          Não usei
        </button>
      </div>
    </div>
  )
}

interface AIChatProps {
  readonly className?: string
  readonly workOrderContext?: WorkOrderContext
  readonly onExtractWorkOrder?: () => void
}

export function AIChat({ className, workOrderContext, onExtractWorkOrder }: Readonly<AIChatProps>) {
  // Log para debug
  useEffect(() => {
    console.log('🤖 AIChat renderizado com workOrderContext:', workOrderContext)
  }, [workOrderContext])
  
  const { messages, isLoading, sendMessage, extractWorkOrderData, solutionOptions, selectSolutionOption, rejectAllSolutionOptions, conversationId, submitFeedback, requestDetailedResponse } = useAIChat(workOrderContext)
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [typingMessageIds, setTypingMessageIds] = useState<Set<string>>(new Set())
  const [displayedMessageIds, setDisplayedMessageIds] = useState<Set<string>>(new Set()) // Track messages that have been fully displayed
  
  // Estado para modal de resposta detalhada
  const [showDetailedModal, setShowDetailedModal] = useState(false)
  const [pendingDetailedMessageId, setPendingDetailedMessageId] = useState<number | null>(null)
  const [isRequestingDetailed, setIsRequestingDetailed] = useState(false)
  
  // Handler para quando clicam "Não usei"
  const handleRequestDetailed = (messageId: number) => {
    setPendingDetailedMessageId(messageId)
    setShowDetailedModal(true)
  }
  
  // Handler para confirmar resposta detalhada
  const handleConfirmDetailed = async () => {
    if (!pendingDetailedMessageId) return
    
    const messageId = pendingDetailedMessageId
    
    // Fechar modal imediatamente - não esperar pela resposta
    setShowDetailedModal(false)
    setPendingDetailedMessageId(null)
    setIsRequestingDetailed(false)
    
    // Processar em background (não bloquear UI)
    try {
      // Primeiro, submeter feedback como "não usou"
      await submitFeedback(messageId, false)
      
      // Depois, solicitar resposta detalhada (pode demorar)
      if (requestDetailedResponse) {
        // Não aguardar - deixar processar em background
        requestDetailedResponse(messageId).catch((error) => {
          console.error('Erro ao solicitar resposta detalhada:', error)
          // Erro já é tratado dentro de requestDetailedResponse
        })
      }
    } catch (error) {
      console.error('Erro ao submeter feedback:', error)
      // Não mostrar alert - apenas logar o erro
    }
  }
  
  // Handler para fechar modal (não quer resposta detalhada)
  const handleCloseDetailedModal = async () => {
    if (pendingDetailedMessageId) {
      // Submeter feedback como "não usou" sem solicitar resposta detalhada
      try {
        await submitFeedback(pendingDetailedMessageId, false)
      } catch (error) {
        console.error('Erro ao enviar feedback:', error)
      }
    }
    setShowDetailedModal(false)
    setPendingDetailedMessageId(null)
  }
  
  // Log para debug
  useEffect(() => {
    console.log('🤖 AIChat estado:', {
      messagesCount: messages.length,
      isLoading,
      conversationId,
      solutionOptionsCount: solutionOptions.length
    })
  }, [messages.length, isLoading, conversationId, solutionOptions.length])

  // Mark the last assistant message for typewriter effect ONLY if it hasn't been displayed yet
  useEffect(() => {
    const lastAssistantMessage = [...messages].reverse().find((msg) => msg.role === 'assistant')
    if (lastAssistantMessage && !isLoading) {
      const messageIndex = messages.indexOf(lastAssistantMessage)
      const messageKey = `${(lastAssistantMessage as any).messageId ?? lastAssistantMessage.timestamp}`
      // Only add to typing if:
      // 1. It hasn't been displayed yet (not in displayedMessageIds)
      // 2. It's not marked as already displayed (_alreadyDisplayed flag)
      // 3. It's the last message (new message just received)
      const isNewMessage = messageIndex === messages.length - 1 || 
                          (messageIndex === messages.length - 2 && messages[messages.length - 1]?.role === 'user')
      if (!displayedMessageIds.has(messageKey) && 
          !(lastAssistantMessage as any)._alreadyDisplayed && 
          isNewMessage) {
        setTypingMessageIds((prev) => new Set([...prev, messageKey]))
      }
    }
  }, [messages, isLoading, displayedMessageIds])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const messageToSend = input.trim()
    setInput('')
    await sendMessage(messageToSend)
    inputRef.current?.focus()
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Fallback: Se não houver mensagens e não estiver a carregar, mostrar mensagem inicial
  const displayMessages = messages.length > 0 ? messages : [
    {
      role: 'assistant' as const,
      content: workOrderContext?.workOrderId
        ? 'Está com problemas? Posso ajudá-lo com um passo a passo para esta viatura.'
        : 'Está com problemas? Posso ajudá-lo com um passo a passo para esta viatura.',
      timestamp: new Date().toISOString(),
    }
  ]

  return (
    <div className={cn('flex flex-col h-full bg-white rounded-lg shadow-sm', className)}>
      {/* Modal para resposta detalhada */}
      <DetailedResponseModal
        isOpen={showDetailedModal}
        onClose={handleCloseDetailedModal}
        onConfirm={handleConfirmDetailed}
        isLoading={isRequestingDetailed}
      />
      
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {displayMessages.map((message) => (
          <div
            key={message.messageId ?? message.timestamp}
            className={cn('flex items-start space-x-3', {
              'flex-row-reverse space-x-reverse': message.role === 'user',
            })}
          >
            <div
              className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center', {
                'bg-primary-600 text-white': message.role === 'assistant',
                'bg-secondary-600 text-white': message.role === 'user',
              })}
            >
              {message.role === 'assistant' ? (
                <Bot className="w-5 h-5" />
              ) : (
                <User className="w-5 h-5" />
              )}
            </div>
            <div
              className={cn('flex-1 rounded-lg px-4 py-2 max-w-[80%]', {
                'bg-primary-50 text-primary-900': message.role === 'assistant',
                'bg-secondary-100 text-secondary-900': message.role === 'user',
              })}
            >
              {message.role === 'assistant' && 
               typingMessageIds.has(`${(message as any).messageId ?? message.timestamp}`) && 
               !(message as any)._alreadyDisplayed ? (
                <TypewriterText
                  text={message.content}
                  speed={5}
                  maxDuration={(message as any)._isHistorical ? 5000 : undefined} // 5 segundos máximo para histórico
                  className="text-sm"
                  onComplete={() => {
                    const messageKey = `${(message as any).messageId ?? message.timestamp}`
                    setTypingMessageIds((prev) => {
                      const next = new Set(prev)
                      next.delete(messageKey)
                      return next
                    })
                    // Mark this message as fully displayed so it won't show typewriter effect again
                    setDisplayedMessageIds((prev) => new Set([...prev, messageKey]))
                  }}
                  renderMarkdown={true}
                />
              ) : (
                <MarkdownRenderer content={message.content} className="text-sm" />
              )}
              {message.timestamp && (
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
              {/* Feedback section for assistant messages */}
              {message.role === 'assistant' && message.messageId && !typingMessageIds.has(`${(message as any).messageId ?? message.timestamp}`) && (
                <FeedbackButtons
                  messageId={message.messageId}
                  feedbackUsed={message.feedbackUsed}
                  feedbackWorked={message.feedbackWorked}
                  onSubmitFeedback={submitFeedback}
                  onRequestDetailed={handleRequestDetailed}
                />
              )}
            </div>
          </div>
        ))}
        {/* Always show typing indicator when loading - ensures chat bubble is always visible */}
        {isLoading && (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div className="flex-1 rounded-lg px-4 py-2 bg-primary-50">
              <TypingIndicator />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Solution Options Display */}
      {solutionOptions.length > 0 && (
        <div className="border-t border-gray-200 p-4 bg-yellow-50">
          <div className="mb-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-yellow-800 mb-1">
              ⚠️ Opções de Solução Encontradas pela IA
            </p>
            <p className="text-xs text-yellow-700">
              Estas são apenas possibilidades sugeridas pela IA. Não são garantias absolutas. Sempre verifique fisicamente antes de proceder.
            </p>
          </div>
          <div className="space-y-2">
            {solutionOptions.map((option, idx) => {
              const status = option.status || 1
              const isPending = status === 1
              const isConfirmed = status === 2
              const isRejected = status === 3

              return (
                <div
                  key={option.id || idx}
                  className={`border rounded-lg p-3 ${
                    isConfirmed
                      ? 'border-green-300 bg-green-50'
                      : isRejected
                      ? 'border-red-200 bg-red-50 opacity-60'
                      : 'border-yellow-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-gray-900">
                          OPÇÃO {option.optionNumber}: {option.diagnosis}
                        </span>
                        {option.successProbability && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                            {Math.round(option.successProbability * 100)}% sucesso
                          </span>
                        )}
                        {isConfirmed && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded font-semibold">
                            ✓ CONFIRMADA
                          </span>
                        )}
                        {isRejected && (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-800 rounded">
                            ✗ REJEITADA
                          </span>
                        )}
                        {isPending && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                            ⏳ PENDENTE
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{option.description}</p>
                      <div className="flex gap-4 text-xs text-gray-500">
                        {option.estimatedCost && (
                          <span>💰 €{option.estimatedCost.toFixed(2)}</span>
                        )}
                      </div>
                      {option.partsNeeded && (
                        <p className="text-xs text-gray-600 mt-1">
                          <strong>Peças:</strong> {option.partsNeeded}
                        </p>
                      )}
                      {option.servicesNeeded && (
                        <p className="text-xs text-gray-600 mt-1">
                          <strong>Serviços:</strong> {option.servicesNeeded}
                        </p>
                      )}
                    </div>
                    {isPending && (
                      <button
                        onClick={async () => {
                          try {
                            if (!option.id) {
                              console.error('Opção sem ID, não é possível confirmar. Aguarde o carregamento das opções.')
                              return
                            }
                            await selectSolutionOption(option.id)
                            if (onExtractWorkOrder) {
                              onExtractWorkOrder()
                            }
                          } catch (error) {
                            console.error('Erro ao selecionar opção:', error)
                            alert('Erro ao confirmar opção. Tente novamente.')
                          }
                        }}
                        className="ml-3 px-3 py-1 text-xs font-semibold bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!option.id}
                      >
                        Confirmar
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {solutionOptions.some((opt) => (opt.status || 1) === 1) && (
            <div className="mt-3 pt-3 border-t border-yellow-300">
              <button
                onClick={async () => {
                  try {
                    if (!conversationId) {
                      alert('Aguarde o carregamento da conversa.')
                      return
                    }
                    await rejectAllSolutionOptions()
                  } catch (error) {
                    console.error('Erro ao rejeitar opções:', error)
                    alert('Erro ao rejeitar opções. Tente novamente.')
                  }
                }}
                className="w-full px-4 py-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!conversationId}
              >
                Nenhuma opção me parece viável
              </button>
            </div>
          )}
        </div>
      )}

      {/* Action buttons if work order context exists */}
      {workOrderContext?.workOrderId && messages.length > 2 && onExtractWorkOrder && solutionOptions.length === 0 && (
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={async () => {
              try {
                const extracted = await extractWorkOrderData()
                if (extracted.options && extracted.options.length > 0) {
                  // Options will be displayed automatically
                } else {
                  onExtractWorkOrder()
                }
              } catch (error: any) {
                console.error('Erro ao extrair dados:', error)
              }
            }}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
          >
            <FileText className="w-4 h-4" />
            Extrair opções de solução da conversa
          </button>
        </div>
      )}

      {/* Input Area - Always visible, disabled during loading */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isLoading ? "Aguarde a resposta..." : "Escreva aqui..."}
            disabled={isLoading}
            readOnly={isLoading}
            className="flex-1 bg-[#17253C] text-white rounded-lg px-6 py-4 text-lg placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className={cn(
              'p-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              {
                'cursor-pointer': input.trim() && !isLoading,
              }
            )}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

